import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API, useAuth } from '../App';
import { Activity, Zap } from 'lucide-react';

/**
 * Topbar Live Cost Meter.
 * Polls /api/cost-meter every 4s. On a new query event:
 *   - if cost > 0 → PAID PULSE (orange, beating)
 *   - if cost == 0 → FREE FLASH (green, expanding ring)
 * Shows today's running ₹ spend and today's free %.
 */
export default function LiveCostMeter() {
  const { auth } = useAuth();
  const [m, setM] = useState(null);
  const [flash, setFlash] = useState(null); // 'paid' | 'free' | null
  const lastIdRef = useRef(null);
  const flashTimer = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const { data } = await axios.get(`${API}/cost-meter`);
        if (cancelled) return;
        // Trigger animation when a NEW query arrives
        const latestId = data.latest?.id;
        if (latestId && latestId !== lastIdRef.current && lastIdRef.current !== null) {
          const isPaid = (data.latest.cost_inr || 0) > 0;
          setFlash(isPaid ? 'paid' : 'free');
          clearTimeout(flashTimer.current);
          flashTimer.current = setTimeout(() => setFlash(null), 1400);
        }
        lastIdRef.current = latestId || null;
        setM(data);
      } catch {
        /* silent */
      }
    };
    tick();
    const id = setInterval(tick, 4000);
    return () => { cancelled = true; clearInterval(id); clearTimeout(flashTimer.current); };
  }, [auth?.token]);

  if (!m) return null;

  const isPaidPulse = flash === 'paid';
  const isFreeFlash = flash === 'free';

  return (
    <div
      data-testid="live-cost-meter"
      className={`relative flex items-center gap-3 border rounded-sm px-3 py-1.5 transition-all duration-300 ${
        isPaidPulse
          ? 'border-[#C17F59] bg-[#C17F59]/15 shadow-[0_0_18px_rgba(193,127,89,0.45)]'
          : isFreeFlash
            ? 'border-emerald-400/60 bg-emerald-400/10 shadow-[0_0_18px_rgba(16,185,129,0.35)]'
            : 'border-white/10 bg-white/[0.02]'
      }`}
    >
      {/* expanding ring on free hit */}
      {isFreeFlash && (
        <span className="absolute inset-0 rounded-sm border-2 border-emerald-400 animate-ping pointer-events-none" />
      )}

      <div className="flex items-center gap-1.5">
        {isPaidPulse ? (
          <Activity size={12} className="text-[#DDA079] animate-pulse" />
        ) : (
          <Zap size={12} className={isFreeFlash ? 'text-emerald-400' : 'text-slate-500'} />
        )}
        <span className="data-label">Today</span>
      </div>

      <div className="flex items-baseline gap-1 border-l border-white/10 pl-3">
        <span className="font-mono text-[14px] text-white tracking-tight" data-testid="meter-spend">
          ₹{m.today_spend_inr.toFixed(2)}
        </span>
        <span className="data-label">spend</span>
      </div>

      <div className="flex items-baseline gap-1 border-l border-white/10 pl-3" data-testid="meter-queries">
        <span className="font-mono text-[14px] text-white">{m.today_queries}</span>
        <span className="data-label">queries</span>
      </div>

      <div className="flex items-baseline gap-1 border-l border-white/10 pl-3">
        <span
          className={`font-mono text-[14px] ${m.today_free_pct >= 70 ? 'text-emerald-400' : m.today_free_pct >= 40 ? 'text-[#DDA079]' : 'text-slate-300'}`}
          data-testid="meter-free-pct"
        >
          {m.today_free_pct.toFixed(0)}%
        </span>
        <span className="data-label">free</span>
      </div>

      {/* latest route badge - subtle */}
      {m.latest && (
        <div className="border-l border-white/10 pl-3 hidden xl:block" data-testid="meter-latest-route">
          <span className="data-label">Last:</span>{' '}
          <span className={`font-mono text-[11px] ${m.latest.cost_inr === 0 ? 'text-emerald-400' : 'text-[#DDA079]'}`}>
            {m.latest.route} · ₹{m.latest.cost_inr.toFixed(2)}
          </span>
        </div>
      )}
    </div>
  );
}
